import { Injectable } from '@angular/core';

import { DESKTOP_NOTIFICATION_PREFERENCE_KEY } from 'src/app/shared/preserved-storage-keys';

interface IDesktopNotificationPreferences {
  users: Record<string, boolean>;
}

/**
 * Per-user desktop-notification opt-in, held as one map under one preserved key.
 * A per-user key would be unpreservable at logout, because the user id itself comes
 * from `currentUser`, which logout destroys.
 */
@Injectable({ providedIn: 'root' })
export class DesktopNotificationPreferenceService {
  isEnabled(userId: number | null): boolean {
    if (userId === null) {
      return false;
    }
    return this.read().users[String(userId)] === true;
  }

  setEnabled(userId: number | null, enabled: boolean): void {
    if (userId === null) {
      return;
    }
    const current = this.read();
    this.write({ users: { ...current.users, [String(userId)]: enabled } });
  }

  // ── Storage ────────────────────────────────────────────────────────────────

  private read(): IDesktopNotificationPreferences {
    try {
      const raw = localStorage.getItem(DESKTOP_NOTIFICATION_PREFERENCE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && parsed.users) {
        return parsed as IDesktopNotificationPreferences;
      }
    } catch {
      // fall through to empty state on malformed JSON / disabled storage
    }
    return { users: {} };
  }

  private write(preferences: IDesktopNotificationPreferences): void {
    try {
      localStorage.setItem(DESKTOP_NOTIFICATION_PREFERENCE_KEY, JSON.stringify(preferences));
    } catch {
      // ignore quota / disabled storage errors — the preference is best-effort
    }
  }
}
